var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from "react";
import { Typography, Switch, Tooltip, Chip, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { PageContainer, PageContent } from "../../components/wraper";
import { SecondaryGrid } from "@ui/components/grid";
import { GridFilters } from "../../components/grid/GridFilters";
import PageHeader from "@ui/components/header/PageHeader";
import { SButton } from "@ui/components/buttons/SButton";
import { useUsersFilter } from "./hooks/useUsersFilter";
import { useUsersActions } from "./hooks/useUsersActions";
import { useTranslation } from "react-i18next";
import { SMenuTrigger, SMenu } from "@ui/components/menu/SMenu";
import { GridCellStack } from "@ui/components/grid";
import { formatUtcToLocalDateTimeString } from "@application/common/helpers/date-util";
import { useDispatch } from "react-redux";
import "@reducers/injectReducters";
import { useAppSelector } from "@application/store/useStore";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { getRolesDropdown } from "@reducers/roles/thunk/role-thunk";
import { getAccessPointsDropdown } from "@reducers/access-points/thunk/access-point-thunk";
import { userEditStorage } from "@utility/user-edit-storage";
import { startImpersonation } from "@reducers/auth/thunk/auth";
import userService from "@utility/user-service";
import { toast } from "@ui/utils/toaster";
import { PermissionChecker, Permission } from "@utility/permission-checker";
import { UserCard } from "./components/UserCard";
import { setPagination, setSelectedUserRow, updateFilters, } from "@reducers/users/slice/user-slice";
const UsersPage = () => {
    var _a, _b;
    const { t } = useTranslation(["form", "validation", "common"]);
    const navigate = useNavigate();
    // Simple menu state - minimal addition
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [selectedTab, setSelectedTab] = useState("all");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [sortState, setSortState] = useState(null);
    // Use existing custom hooks
    const { searchTerm, filters, paginationInfo, handleFilterChange, handleSearchChange, handleToggleActive, handleClearFilters, } = useUsersFilter(sortState, selectedTab);
    const { handleAddUser, handleEditUser, handleDeleteUser, handleViewUser } = useUsersActions();
    // Check if current user is Super Admin
    const isSuperAdmin = userService.hasRole("Super Admin");
    const isImpersonating = useAppSelector((state) => state.auth.impersonation.isImpersonating);
    // Handle impersonation
    const handleImpersonate = (userId) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield dispatch(startImpersonation(userId))
                .unwrap()
                .then(() => {
                toast.success(t("common:toasts.impersonationStarted"));
                // Navigate to dashboard to see the impersonated user's view
                navigate("/dashboard");
            })
                .catch((error) => {
                toast.error(error || t("common:toasts.failedToStartImpersonation"));
            });
            handleMenuClose();
        }
        catch (error) {
            console.error("Error starting impersonation:", error);
        }
    });
    // Handle row click - navigate to view profile
    const handleRowClick = (params) => {
        const selectedRowData = users === null || users === void 0 ? void 0 : users.find((user) => user.id === params.id);
        if (selectedRowData) {
            dispatch(setSelectedUserRow(selectedRowData));
        }
        // Navigate to user profile on row click (impersonation only via button)
        handleViewUser(params.id);
    };
    // Simple menu handlers
    const handleMenuClick = (event, userId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setSelectedUserId(userId);
    };
    const dispatch = useDispatch();
    const { users, loading: usersLoading, deleteLoading, } = useAppSelector((state) => state.user);
    const { rolesDropdown, dropdownLoading } = useAppSelector((state) => state.role);
    const { accessPointsDropdown, dropdownLoading: apDropdownLoading } = useAppSelector((state) => state.accessPoint);
    // Only fetch if not already loaded and not currently loading
    React.useEffect(() => {
        if (!rolesDropdown && !dropdownLoading) {
            dispatch(getRolesDropdown());
        }
    }, [dispatch, rolesDropdown, dropdownLoading]);
    React.useEffect(() => {
        if (!accessPointsDropdown && !apDropdownLoading) {
            dispatch(getAccessPointsDropdown());
        }
    }, [dispatch, accessPointsDropdown, apDropdownLoading]);
    const roleOptions = React.useMemo(() => {
        if (!rolesDropdown)
            return [];
        return rolesDropdown.map((role) => ({ value: role.id, label: role.name }));
    }, [rolesDropdown]);
    const accessPointFilterOptions = React.useMemo(() => {
        if (!accessPointsDropdown)
            return [];
        return accessPointsDropdown.map((ap) => ({
            value: ap.id,
            label: ap.name || ap.id,
        }));
    }, [accessPointsDropdown]);
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setSelectedUserId(null);
    };
    const handleDeleteClick = (userId) => {
        setUserToDelete(userId);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };
    const handleConfirmDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (userToDelete) {
            yield handleDeleteUser(userToDelete);
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        }
    });
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };
    const handleSortChange = (field, order) => {
        setSortState({ field, order });
    };
    const handleTabChange = (tabValue) => {
        setSelectedTab(tabValue);
        setSortState(null); // Reset sorting when tab changes
        // Determine isActive value based on the new tab
        let isActiveValue = null;
        if (tabValue === "active") {
            isActiveValue = true;
        }
        else if (tabValue === "inactive") {
            isActiveValue = false;
        }
        // Clear all filters and set only isActive based on the new tab
        dispatch(updateFilters({
            name: undefined,
            userName: undefined,
            userId: undefined,
            roleIds: [],
            lastLoginAt: undefined,
            accessPointUids: [],
            isActive: isActiveValue,
            globalSearch: undefined,
        }));
    };
    const menuItems = selectedUserId
        ? [
            ...(PermissionChecker.hasPermission(Permission.USERS_EDIT)
                ? [
                    {
                        label: t("common:actions.edit"),
                        onClick: () => {
                            const selectedRowData = users === null || users === void 0 ? void 0 : users.find((user) => user.id === selectedUserId);
                            if (selectedRowData) {
                                dispatch(setSelectedUserRow(selectedRowData));
                                userEditStorage.saveUserEditData(Object.assign(Object.assign({}, selectedRowData), { selectedRole: selectedRowData.roleIds[0] || "" }));
                            }
                            handleEditUser(selectedUserId);
                            handleMenuClose();
                        },
                    },
                ]
                : []),
            ...(isSuperAdmin
                ? [
                    {
                        label: t("common:actions.impersonate"),
                        disabled: isImpersonating,
                        onClick: () => {
                            handleImpersonate(selectedUserId);
                        },
                    },
                ]
                : []),
            ...(PermissionChecker.hasPermission(Permission.USERS_DELETE)
                ? [
                    {
                        label: t("common:actions.delete"),
                        onClick: () => {
                            handleDeleteClick(selectedUserId);
                        },
                        color: "error",
                    },
                ]
                : []),
        ]
        : [];
    const columns = [
        {
            field: "name",
            headerName: t("common:grid.user"),
            flex: 2,
            minWidth: 200,
            filterable: true,
            filterType: "text",
            sortable: true,
            renderCell: (params) => (React.createElement(GridCellStack, { topText: `${params.row.firstName} ${params.row.lastName}`, bottomText: params.row.email })),
        },
        {
            field: "userId",
            headerName: t("common:grid.userId"),
            flex: 1,
            minWidth: 100,
            filterable: true,
            filterType: "text",
            sortable: true,
        },
        {
            field: "roleNames",
            headerName: t("common:grid.access"),
            flex: 1,
            minWidth: 120,
            filterable: true,
            filterType: "select",
            sortable: false,
            filterOptions: roleOptions.filter((role) => role.value !== ""),
            renderCell: (params) => {
                const roleNames = params.row.roleNames || [];
                return (React.createElement(Typography, { component: "span" }, roleNames.length > 0
                    ? roleNames.join(", ")
                    : t("common:status.noRole")));
            },
        },
        {
            field: "assignedAccessPoints",
            headerName: t("common:grid.assignedAps"),
            flex: 1.2,
            minWidth: 140,
            filterable: true,
            filterField: "accessPointUids",
            filterType: "select",
            sortable: false,
            filterOptions: accessPointFilterOptions,
            renderCell: (params) => {
                const aps = params.row
                    .assignedAccessPoints;
                if (!(aps === null || aps === void 0 ? void 0 : aps.length)) {
                    return (React.createElement(Typography, { component: "span", color: "text.secondary" }, "\u2014"));
                }
                const firstName = (aps[0].name || aps[0].uid).trim();
                const maxLen = 18;
                const short = firstName.length > maxLen
                    ? `${firstName.slice(0, maxLen)}...`
                    : firstName;
                const rest = aps.length - 1;
                const tooltipLines = aps
                    .map((ap) => `${ap.uid} (${(ap.name || ap.uid).trim()})`)
                    .join("\n");
                return (React.createElement(Tooltip, { title: React.createElement(Typography, { component: "div", variant: "body2", sx: {
                            whiteSpace: "pre-line",
                            maxHeight: 260,
                            overflowY: "auto",
                            display: "block",
                        } }, tooltipLines) },
                    React.createElement(Stack, { direction: "row", alignItems: "center", spacing: 0.75, sx: { minWidth: 0, maxWidth: "100%" } },
                        React.createElement(Typography, { noWrap: true, component: "span", sx: { minWidth: 0 } }, short),
                        rest > 0 ? (React.createElement(Chip, { label: `+${rest}`, size: "small", variant: "outlined", sx: {
                                height: 22,
                                flexShrink: 0,
                                "& .MuiChip-label": { px: 0.75, fontSize: "0.75rem" },
                            } })) : null)));
            },
        },
        {
            field: "lastLoginAt",
            headerName: t("common:grid.lastVisit"),
            flex: 1.2,
            minWidth: 150,
            filterable: true,
            filterType: "dateRange",
            sortable: true,
            renderCell: (params) => (React.createElement(Typography, { component: "span" }, params.row.lastLoginAt
                ? formatUtcToLocalDateTimeString(params.row.lastLoginAt, false)
                : t("common:actions.never"))),
        },
        {
            field: "isActive",
            headerName: t("common:grid.active"),
            flex: 0.8,
            minWidth: 80,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                var _a;
                return (React.createElement(Switch, { checked: params.row.isActive, onChange: () => handleToggleActive(params.row), onClick: (e) => e.stopPropagation(), disabled: ((_a = userService.getUser()) === null || _a === void 0 ? void 0 : _a.id) === params.row.id ||
                        !PermissionChecker.hasPermission(Permission.USERS_EDIT), size: "small" }));
            },
        },
        {
            field: "actions",
            headerName: t("common:grid.actions"),
            flex: 0.5,
            minWidth: 80,
            filterable: false,
            renderCell: (params) => (React.createElement(SMenuTrigger, { onClick: (event) => handleMenuClick(event, params.row.id), size: "medium", color: "default" })),
        },
    ];
    return (React.createElement(PageContainer, null,
        React.createElement(PageHeader, { title: t("tabs.users"), topActions: PermissionChecker.hasPermission(Permission.USERS_CREATE) ? (React.createElement(SButton, { variant: "contained", size: "large", onClick: handleAddUser }, t("common:actions.addUser"))) : undefined }),
        React.createElement(PageContent, null,
            React.createElement(GridFilters, { title: `${(_b = (_a = paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) !== null && _a !== void 0 ? _a : users === null || users === void 0 ? void 0 : users.length) !== null && _b !== void 0 ? _b : 0} ${t("common:headings.users")}`, columns: columns, filters: filters, onChange: handleFilterChange, searchProp: searchTerm, handleSearchChange: handleSearchChange, tabs: [
                    { label: t("common:tabs.allUsers"), value: "all" },
                    { label: t("common:tabs.active"), value: "active" },
                    { label: t("common:tabs.inactive"), value: "inactive" },
                ], selectedTab: selectedTab, onTabChange: handleTabChange, onClearFilters: handleClearFilters, sortState: sortState, onSortChange: handleSortChange }),
            React.createElement(SecondaryGrid, { rows: usersLoading ? [] : users || [], columns: columns, loading: usersLoading, onRowClick: handleRowClick, paginationMode: "server", rowCount: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.totalCount) || 0, paginationModel: {
                    page: ((paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageNumber) || 1) - 1,
                    pageSize: (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageSize) || 10,
                }, onPaginationModelChange: (model) => {
                    const newPageNumber = model.page + 1;
                    const newPageSize = model.pageSize;
                    if (newPageNumber !== (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageNumber) ||
                        newPageSize !== (paginationInfo === null || paginationInfo === void 0 ? void 0 : paginationInfo.pageSize)) {
                        dispatch(setPagination({
                            pageNumber: newPageNumber,
                            pageSize: newPageSize,
                        }));
                    }
                }, disableColumnSorting: true, pageSizeOptions: [25, 50], renderCard: (row) => {
                    var _a;
                    return (React.createElement(UserCard, { row: row, onClick: () => handleRowClick({ id: row.id }), onMenuClick: handleMenuClick, onToggleActive: handleToggleActive, canEdit: PermissionChecker.hasPermission(Permission.USERS_EDIT), isCurrentUser: ((_a = userService.getUser()) === null || _a === void 0 ? void 0 : _a.id) === row.id }));
                } }),
            React.createElement(SMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), handleClose: handleMenuClose, menuItems: menuItems }),
            React.createElement(ConfirmDialog, { isOpen: deleteDialogOpen, onClose: handleCloseDeleteDialog, onConfirm: handleConfirmDelete, loading: deleteLoading, actionText: t("common:actions.delete"), screenName: t("common:labels.user"), variant: "delete" }))));
};
export default UsersPage;
